import React, { useEffect, useState } from "react";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  Box, Typography, Grid, Chip, Button, CircularProgress, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, LinearProgress,
  Checkbox, Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ProcurementPremiumHeader from "../Shared/PremiumHeader";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface DashboardData {
  total_pos: number; pending_pos: number; month_spend: number; total_suppliers: number;
  open_alerts: number; critical_alerts: number; reorder_needed: number;
  top_suppliers: any[]; recent_alerts: any[];
}

interface ReorderSuggestion {
  product_id: number; product_name: string; current_stock: number; reorder_point: number;
  purchase_need: number; priority: string; recommended_supplier: any; estimated_cost: number;
}

const SEVERITY = { critical: { bg: "#FEE2E2", color: "#DC2626", icon: <ErrorIcon sx={{ fontSize: 14 }} /> }, warning: { bg: "#FEF3C7", color: "#D97706", icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> }, info: { bg: "#DBEAFE", color: "#2563EB", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> } };
const PRIORITY = { critical: { bg: "#FEE2E2", color: "#DC2626" }, high: { bg: "#FEF3C7", color: "#D97706" }, medium: { bg: "#DBEAFE", color: "#2563EB" } };

const ProcurementDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [suggestions, setSuggestions] = useState<ReorderSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const [payables, setPayables] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [creatingPOs, setCreatingPOs] = useState(false);
  const [createdResult, setCreatedResult] = useState<any>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [extRes, payRes] = await Promise.all([
        fetch(`${API_URL}/procurement/dashboard-extended`, { method: "POST", headers: getHeaders() }),
        fetch(`${API_URL}/procurement/payables-summary`, { headers: getHeaders() }),
      ]);
      const [extJson, payJson] = await Promise.all([extRes.json(), payRes.json()]);
      if (extJson.success) {
        setData(extJson.data);
        setSuggestions(extJson.data.reorder_suggestions || []);
      }
      if (payJson.success) setPayables(payJson.data);
    } catch {} finally { setLoading(false); }
  };

  const handleGeneratePOs = async () => {
    const items = suggestions.filter((_, i) => selectedItems.has(i)).map(s => ({
      product_id: s.product_id,
      purchase_need: s.purchase_need,
      supplier_id: s.recommended_supplier?.supplier_id,
    }));
    if (items.length === 0) return;
    setCreatingPOs(true);
    try {
      const res = await fetch(`${API_URL}/procurement/generate-pos`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ items }),
      });
      const json = await res.json();
      if (json.success) {
        setCreatedResult(json.data);
        setSelectedItems(new Set());
        loadData();
      }
    } catch {} finally { setCreatingPOs(false); }
  };

  const toggleItem = (idx: number) => {
    setSelectedItems(prev => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  };
  const selectAll = () => {
    if (selectedItems.size === suggestions.length) setSelectedItems(new Set());
    else setSelectedItems(new Set(suggestions.map((_, i) => i)));
  };

  const generateAlerts = async () => {
    setGenerating(true);
    try {
      await fetch(`${API_URL}/procurement/alerts/generate`, { method: "POST", headers: getHeaders() });
      loadData();
    } catch {} finally { setGenerating(false); }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) return <PageLoader height="40vh" />;

  return (
    <Box sx={{ bgcolor: "#F6F8FA", minHeight: "100vh", p: 3 }}>
      <ProcurementPremiumHeader
        title="Procurement Hub"
        subtitle="Supplier intelligence, reorder automation, and spend analytics — updates live"
        icon={<TrendingUpIcon />}
        badge="AI"
        onRefresh={loadData}
        actions={[
          { label: "Scan Alerts", icon: generating ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NotificationsIcon sx={{ fontSize: 16 }} />, onClick: generateAlerts, disabled: generating, variant: "outline" },
          { label: "New Purchase Order", icon: <AddIcon sx={{ fontSize: 16 }} />, onClick: () => navigate("/bill/create"), variant: "primary" },
        ]}
        kpis={[
          { label: "Purchase Orders",  value: data?.total_pos || 0,                                                  sub: `${data?.pending_pos || 0} pending`,        icon: <ShoppingCartIcon />, tone: "blue" },
          { label: "Month Spend",      value: `$${(data?.month_spend || 0).toLocaleString()}`,                       sub: "This month",                                icon: <TrendingUpIcon />,  tone: "emerald" },
          { label: "Suppliers",        value: data?.total_suppliers || 0,                                            sub: "Active vendors",                            icon: <StorefrontIcon />,  tone: "cyan" },
          { label: "Reorder Needed",   value: data?.reorder_needed || 0,                                             sub: "Below threshold",                           icon: <InventoryIcon />,    tone: "gold",   urgent: (data?.reorder_needed || 0) > 0 },
          { label: "Outstanding",      value: `$${(payables?.total_payable || 0).toLocaleString()}`,                 sub: "Payable to suppliers",                      icon: <AttachMoneyIcon />, tone: "red" },
          { label: "AI Savings",       value: `$${((data as any)?.savings?.total_savings || 0).toLocaleString()}`,   sub: "This month",                                icon: <AutoAwesomeIcon />, tone: "purple" },
        ]}
      />

      <Box>
        {/* KPI strip removed — now lives inside the gradient header above */}

        <Grid container spacing={3}>
          {/* Reorder Suggestions */}
          <Grid item xs={12} md={8}>
            <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", overflow: "hidden" }}>
              <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 1.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: "#7C3AED" }} />
                <Typography fontSize={15} fontWeight={600} color="#111827">AI Reorder Suggestions</Typography>
                <Chip label={suggestions.length} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#EDE9FE", color: "#7C3AED" }} />
                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                  {selectedItems.size > 0 && (
                    <Button size="small" variant="contained" disableElevation onClick={handleGeneratePOs} disabled={creatingPOs}
                      startIcon={creatingPOs ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <LocalShippingIcon sx={{ fontSize: 14 }} />}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
                      Create PO ({selectedItems.size} items)
                    </Button>
                  )}
                  {suggestions.length > 0 && (
                    <Button size="small" variant="outlined" onClick={selectAll}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
                      {selectedItems.size === suggestions.length ? "Deselect All" : "Select All"}
                    </Button>
                  )}
                </Box>
              </Box>
              {/* PO Created Alert */}
              {createdResult && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: "#F0FDF4", borderBottom: "1px solid #D1FAE5" }}>
                  <Alert severity="success" sx={{ borderRadius: 2, fontSize: 12 }} onClose={() => setCreatedResult(null)}>
                    {createdResult.created} PO(s) created — Total: ${createdResult.total_value.toFixed(2)}
                    {createdResult.estimated_savings > 0 ? ` — Est. savings: $${createdResult.estimated_savings.toFixed(2)}` : ""}
                  </Alert>
                </Box>
              )}
              {suggestions.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                        <TableCell sx={{ width: 40, bgcolor: "#F9FAFB" }}>
                          <Checkbox size="small" checked={selectedItems.size === suggestions.length && suggestions.length > 0}
                            indeterminate={selectedItems.size > 0 && selectedItems.size < suggestions.length}
                            onChange={selectAll} sx={{ p: 0 }} />
                        </TableCell>
                        {["Product", "Stock", "Need", "Supplier", "Cost", "Priority"].map(h => (
                          <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280", bgcolor: "#F9FAFB" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suggestions.map((s, i) => {
                        const pr = PRIORITY[s.priority as keyof typeof PRIORITY] || PRIORITY.medium;
                        return (
                          <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F9FAFB" }, bgcolor: selectedItems.has(i) ? "#F0FDFA" : "inherit" }}>
                            <TableCell sx={{ width: 40 }}>
                              <Checkbox size="small" checked={selectedItems.has(i)} onChange={() => toggleItem(i)} sx={{ p: 0 }} />
                            </TableCell>
                            <TableCell><Typography fontSize={13} fontWeight={600} color="#111827" sx={{ textTransform: "capitalize" }}>{s.product_name}</Typography></TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography fontSize={13} fontWeight={700} color={s.current_stock <= 0 ? "#DC2626" : "#D97706"}>{s.current_stock}</Typography>
                                <Typography fontSize={10} color="#9CA3AF">/ {s.reorder_point}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell><Typography fontSize={14} fontWeight={700} color="#111827">{s.purchase_need}</Typography></TableCell>
                            <TableCell>
                              {s.recommended_supplier ? (
                                <Box>
                                  <Typography fontSize={12} fontWeight={600} color="#111827">{s.recommended_supplier.supplier_name}</Typography>
                                  <Typography fontSize={10} color="#9CA3AF">Score: {s.recommended_supplier.score}/100</Typography>
                                </Box>
                              ) : <Typography fontSize={11} color="#9CA3AF">No supplier</Typography>}
                            </TableCell>
                            <TableCell><Typography fontSize={13} fontWeight={600} color="#059669">${s.estimated_cost.toFixed(2)}</Typography></TableCell>
                            <TableCell><Chip label={s.priority} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: pr.bg, color: pr.color, textTransform: "capitalize" }} /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: "#D1FAE5", mb: 1 }} />
                  <Typography fontSize={14} color="#6B7280">All products are well-stocked</Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Panel */}
          <Grid item xs={12} md={4}>
            {/* Top Suppliers */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #E5E7EB", p: 2.5, mb: 3 }}>
              <Typography fontSize={14} fontWeight={600} color="#0B1E2D" mb={2} sx={{ letterSpacing: 0.2 }}>Top Suppliers</Typography>
              {(data?.top_suppliers || []).map((s: any, i: number) => {
                const score = Number(s.score) || 0;
                const barColor = score >= 70 ? "#0B1E2D" : score >= 40 ? "#94A3B8" : "#CBD5E1";
                return (
                  <Box key={i} sx={{ py: 1.5, borderBottom: i < (data?.top_suppliers?.length || 0) - 1 ? "1px solid #F3F4F6" : "none" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
                      <Box sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: "#EEF2F7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography fontSize={11} fontWeight={700} color="#0B1E2D">{i + 1}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontSize={12.5} fontWeight={600} color="#0B1E2D" noWrap>{s.name}</Typography>
                        <Typography fontSize={10.5} color="#94A3B8">${(s.total_spend || 0).toLocaleString()} spend</Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography fontSize={14} fontWeight={700} color="#0B1E2D">{s.score}</Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, score)}
                      sx={{
                        height: 4, borderRadius: 999, bgcolor: "#F1F5F9",
                        "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: barColor },
                      }}
                    />
                  </Box>
                );
              })}
              {(!data?.top_suppliers || data.top_suppliers.length === 0) && (
                <Typography fontSize={12} color="#94A3B8" textAlign="center" py={2}>No performance data yet</Typography>
              )}
            </Box>

            {/* Recent Alerts */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #E5E7EB", p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography fontSize={14} fontWeight={600} color="#0B1E2D" sx={{ letterSpacing: 0.2 }}>Alerts</Typography>
                {(data?.open_alerts || 0) > 0 && (
                  <Chip label={data?.open_alerts} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "#FEE2E2", color: "#DC2626" }} />
                )}
              </Box>
              {(data?.recent_alerts || []).map((a: any, i: number) => {
                const sv = SEVERITY[a.severity as keyof typeof SEVERITY] || SEVERITY.info;
                return (
                  <Box key={i} sx={{ display: "flex", gap: 1, p: 1.25, mb: 1, borderRadius: 2, bgcolor: sv.bg, border: `1px solid ${sv.bg}` }}>
                    <Box sx={{ color: sv.color, mt: 0.25 }}>{sv.icon}</Box>
                    <Box>
                      <Typography fontSize={11} fontWeight={600} color={sv.color}>{a.title}</Typography>
                      <Typography fontSize={10} color="#6B7280">{a.message}</Typography>
                    </Box>
                  </Box>
                );
              })}
              {(!data?.recent_alerts || data.recent_alerts.length === 0) && (
                <Typography fontSize={12} color="#9CA3AF" textAlign="center" py={2}>No alerts</Typography>
              )}
            </Box>

            {/* Expected Deliveries */}
            {(data as any)?.expected_deliveries?.length > 0 && (
              <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", p: 2.5, mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <LocalShippingIcon sx={{ fontSize: 16, color: "#00838f" }} />
                  <Typography fontSize={14} fontWeight={600} color="#111827">Expected Deliveries</Typography>
                </Box>
                {(data as any).expected_deliveries.map((d: any, i: number) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: i < (data as any).expected_deliveries.length - 1 ? "1px solid #F3F4F6" : "none", cursor: "pointer", "&:hover": { bgcolor: "#F9FAFB" }, borderRadius: 1, px: 1 }}
                    onClick={() => navigate(`/po-detail/${d.id}`)}>
                    <Box>
                      <Typography fontSize={12} fontWeight={600} color="#2563EB">{d.po_number}</Typography>
                      <Typography fontSize={10} color="#9CA3AF">{d.supplier}</Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography fontSize={11} color="#374151">{new Date(d.date).toLocaleDateString()}</Typography>
                      <Chip label={d.status} size="small" sx={{ height: 16, fontSize: 8, fontWeight: 600, bgcolor: "#F3F4F6", color: "#6B7280", textTransform: "capitalize" }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Recent GRNs */}
            {(data as any)?.recent_grns?.length > 0 && (
              <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", p: 2.5, mt: 3 }}>
                <Typography fontSize={14} fontWeight={600} color="#111827" mb={2}>Recent Receipts</Typography>
                {(data as any).recent_grns.map((g: any, i: number) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: i < 4 ? "1px solid #F3F4F6" : "none" }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#059669", flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography fontSize={11} fontWeight={500} color="#374151">{g.product} — +{g.qty}</Typography>
                      <Typography fontSize={10} color="#9CA3AF">{g.grn || new Date(g.date).toLocaleDateString()}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProcurementDashboard;
